import React, { useEffect, useState } from 'react';

import { useIsSSROrHydrating } from '@confluence/ssr-utilities/entry-points/useIsSSROrHydrating.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { ConnectorDeepLinkModal } from './ConnectorDeepLinkModal.tsx';
import { useConnectorDeepLink } from './useConnectorDeepLink.ts';

/**
 * Opens the Universal Connector Modal when a Confluence page has `?add-connector`
 * and the user has Rovo. Lazy-loaded by `ConnectorDeepLinkComponentLoader`.
 *
 * Client-only via `useIsSSROrHydrating` (no manual mount-state guard, no
 * hydration mismatch). `everActive` keeps the modal mounted after the param is
 * cleaned up so the post-open URL cleanup can't tear it down; detection stays
 * reactive so a param added live via in-app navigation also opens it.
 */
export const ConnectorDeepLink = (): React.JSX.Element | null => {
	const renderServerPlaceholder = useIsSSROrHydrating();
	const [everActive, setEverActive] = useState(false);
	const { isDeepLinkActive, markHandled } = useConnectorDeepLink();
	// Default to {} so we no-op rather than throw if rendered without a
	// SessionDataContext provider (e.g. in isolation tests).
	const { cloudId, isRovoEnabled } = useSessionData() ?? {};

	useEffect(() => {
		if (isDeepLinkActive) {
			setEverActive(true);
		}
	}, [isDeepLinkActive]);

	if (renderServerPlaceholder || !everActive || !isRovoEnabled || !cloudId) {
		return null;
	}

	return (
		<ConnectorDeepLinkModal cloudId={cloudId} isActive={isDeepLinkActive} onHandled={markHandled} />
	);
};
